import type { VendorLicenseCompletePayload } from "@services/licensing/types";
import { completePortOperatorLicenseById, completeVendorLicenseById } from "@services/licensingApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

type Error = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
};

const getErrDetail = (err: unknown): string | undefined => {
  const axiosDetail = (err as any)?.response?.data?.detail as string | undefined;
  if (axiosDetail) return axiosDetail;

  const directDetail =
    ((err as any)?.data as Error | undefined)?.detail ??
    ((err as any)?.detail as string | undefined);
  if (directDetail) return directDetail;

  return undefined;
};

const getCompleteLicenseAction = (
  payload: VendorLicenseCompletePayload,
  isPortOperatorContext: boolean,
): string => {
  const entityName = isPortOperatorContext ? "port_operator_license" : "vendor_license";
  if (payload.status === "APPROVED") return `approve_${entityName}`;
  if (payload.status === "REJECTED") return `reject_${entityName}`;
  return `complete_${entityName}`;
};

export const useCompleteLicense = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: (
    status?: string,
    variables?: {
      id: string;
      payload: VendorLicenseCompletePayload;
      isPortOperatorContext?: boolean;
    },
  ) => void | Promise<void>;
  handleError?: (err: unknown) => void;
}) => {
  const completeLicenseMutation = useMutation({
    mutationFn: ({
      id,
      payload,
      isPortOperatorContext = false,
    }: {
      id: string;
      payload: VendorLicenseCompletePayload;
      isPortOperatorContext?: boolean;
    }) =>
      runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: getCompleteLicenseAction(payload, isPortOperatorContext),
          entityType: "license_request",
          entityId: id,
        },
        (transactionId) =>
          isPortOperatorContext
            ? completePortOperatorLicenseById({
                id,
                payload,
                ...(transactionId ? { transactionId } : {}),
              })
            : completeVendorLicenseById({
                id,
                payload,
                ...(transactionId ? { transactionId } : {}),
              }),
      ),
    onSuccess: async (_data, variables) => {
      const status = (variables?.payload?.status as string | undefined) ?? undefined;
      await handleSuccess(status, variables);
    },
    onError: (err) => {
      handleError?.(err);
    },
  });

  const completeLicense = ({
    licenseId,
    payload,
    isPortOperatorContext = false,
  }: {
    licenseId: string;
    payload: VendorLicenseCompletePayload;
    isPortOperatorContext?: boolean;
  }) => {
    completeLicenseMutation.mutate({
      id: licenseId,
      payload,
      isPortOperatorContext,
    });
  };

  return {
    completeLicense,
    isPending: completeLicenseMutation.isPending,
    error: completeLicenseMutation.error,
    getErrorDetail: () => getErrDetail(completeLicenseMutation.error),
  };
};
